/**
 * Ad-hoc admin reward — issue a voucher of any amount (up to $500) to an active member, with no
 * pre-configured CTA. Admin-only.
 *
 * This screen moves money, so it is deliberately three steps: choose the member, set the amount,
 * then confirm against a plain-English summary before anything is issued. There is no way to issue
 * from the form directly.
 *
 * IDEMPOTENCY. The key is minted once when the admin reaches the confirmation step and is REUSED on
 * every retry of that same request, so a double-click, a flaky network or an impatient second press
 * cannot mint a second voucher — the server returns the first one. It is regenerated only when the
 * request materially changes (different member or different amount), because the server treats a
 * key reused for a different request as a conflict and refuses it.
 */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { getContacts } from "../services/contacts.service";
import {
  getAdhocRewardMaxAmount,
  issueAdhocReward,
  getAdhocRewardIssuances,
  getScopableRetailLocations,
  ALLOWED_VALIDITY_DAYS,
  AdhocRewardError,
  type AdhocRewardResult,
  type AdhocScopeMode,
} from "../services/adhocReward.service";
import type { Contact } from "../types/contact.types";

// Shown while the real cap loads. Never used to ADMIT an amount — the server decides.
const MAX_AMOUNT_PLACEHOLDER = 150;
const DEFAULT_VALIDITY_DAYS = 90;

type Step = "choose" | "amount" | "confirm" | "done";
type View = "issue" | "history";

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const whenLocal = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

/**
 * The issuance ledger. Append-only and service-role-only at the table level; this reads it through
 * get_adhoc_reward_issuances, which re-checks the caller is an active admin. Shown because a money
 * ledger nobody can read is not an audit trail.
 */
const IssuanceHistory: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["adhoc-reward-issuances"],
    queryFn: () => getAdhocRewardIssuances(50, 0, null),
  });

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading…</p>;
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
        {(error as Error).message}
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-sm text-gray-600">No rewards have been issued yet.</p>
      </div>
    );
  }

  const total = data.reduce((n, r) => n + Number(r.amount ?? 0), 0);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-600">
          {data.length} most recent {data.length === 1 ? "reward" : "rewards"}
        </p>
        <p className="text-sm text-gray-600">
          Total shown{" "}
          <span className="font-semibold text-gray-900">{money(total)}</span>
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Issued</th>
              <th className="text-left px-4 py-2 font-medium">Member</th>
              <th className="text-right px-4 py-2 font-medium">Amount</th>
              <th className="text-left px-4 py-2 font-medium">Voucher</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-left px-4 py-2 font-medium">By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((r) => (
              <tr key={r.id} className="align-top">
                <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                  {whenLocal(r.issued_at)}
                  {r.daily_limit_overridden && (
                    <span
                      className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800"
                      title="A second reward that day, issued deliberately"
                    >
                      override
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">
                    {r.contact_name || "(no name)"}
                  </div>
                  <div className="text-xs text-gray-500">{r.recipient_email}</div>
                  {r.admin_notes && (
                    <div className="text-xs text-gray-400 mt-1 italic">
                      {/* Internal only — the server keeps this off the voucher itself. */}
                      {r.admin_notes}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                  {money(Number(r.amount))}
                </td>
                <td className="px-4 py-3">
                  <div className="font-mono text-xs text-gray-900">
                    {r.voucher_code}
                  </div>
                  {r.expires_at && (
                    <div className="text-xs text-gray-500">
                      expires {whenLocal(r.expires_at)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {r.is_redeemed ? (
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Redeemed
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {r.voucher_status ?? "active"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                  {r.actor_name || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdhocReward: React.FC = () => {
  const [view, setView] = useState<View>("issue");
  const [step, setStep] = useState<Step>("choose");
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [member, setMember] = useState<Contact | null>(null);
  const [amount, setAmount] = useState("");
  const [validityDays, setValidityDays] = useState(String(DEFAULT_VALIDITY_DAYS));
  const [scopeMode, setScopeMode] = useState<AdhocScopeMode>("unscoped");
  const [locationId, setLocationId] = useState("");
  const [notes, setNotes] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [result, setResult] = useState<AdhocRewardResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offerOverride, setOfferOverride] = useState(false);

  const { data: searchResults, isFetching } = useQuery({
    queryKey: ["adhoc-member-search", submittedSearch],
    queryFn: () =>
      getContacts({ page: 1, limit: 15 }, { search: submittedSearch }),
    enabled: submittedSearch.trim().length >= 2,
  });

  const { data: maxAmountRaw } = useQuery({
    queryKey: ["adhoc-reward-max-amount"],
    queryFn: getAdhocRewardMaxAmount,
  });
  const MAX_AMOUNT = maxAmountRaw ?? MAX_AMOUNT_PLACEHOLDER;

  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ["adhoc-retail-locations"],
    queryFn: getScopableRetailLocations,
    enabled: scopeMode === "retailer",
  });

  const amountValue = Number(amount);
  const amountValid =
    Number.isFinite(amountValue) &&
    amountValue >= 0.01 &&
    amountValue <= MAX_AMOUNT &&
    /^\d+(\.\d{1,2})?$/.test(amount.trim());
  const validityValue = Number(validityDays);
  // The server accepts ONLY c_allowed_days. A free-text field let an admin enter 45 and be refused
  // after confirming, so the control is an allowlist and cannot produce a rejected value.
  const validityValid = (ALLOWED_VALIDITY_DAYS as readonly number[]).includes(
    validityValue,
  );
  const scopeValid = scopeMode === "unscoped" || locationId !== "";

  const goToConfirm = () => {
    // Mint the key ONCE for this exact request. Retries reuse it; a changed member or amount
    // produces a new one because the server refuses a key reused for a different request.
    setIdempotencyKey(crypto.randomUUID());
    setOfferOverride(false);
    setError(null);
    setStep("confirm");
  };

  const issue = async (overrideDailyLimit = false) => {
    if (!member || !idempotencyKey) return;
    setIssuing(true);
    setError(null);
    try {
      const res = await issueAdhocReward({
        contactId: member.id,
        amount: amountValue,
        validityDays: validityValue,
        scopeMode,
        scopeRefId: scopeMode === "retailer" ? locationId : null,
        notes: notes.trim() ? notes.trim() : null,
        idempotencyKey,
        overrideDailyLimit,
      });
      setResult(res);
      setStep("done");
    } catch (e) {
      const err = e as AdhocRewardError;
      setError(err.message ?? "The reward could not be issued.");
      // The server refuses a second same-day reward to the same member. That is a real guard, not a
      // failure — offer the deliberate override rather than leaving the admin stuck.
      //
      // Keyed on the MACHINE CODE the edge function returns, not the prose. This previously matched
      // /already received|daily/i against the message, which would have silently stopped offering
      // the override the moment anyone reworded it — and the override is the only way out of the
      // refusal, so the admin would have been stuck with no indication why.
      setOfferOverride(err.code === "daily_limit_reached");
    } finally {
      setIssuing(false);
    }
  };

  const reset = () => {
    setStep("choose");
    setSearch("");
    setSubmittedSearch("");
    setMember(null);
    setAmount("");
    setValidityDays(String(DEFAULT_VALIDITY_DAYS));
    setScopeMode("unscoped");
    setLocationId("");
    setNotes("");
    setIdempotencyKey(null);
    setResult(null);
    setError(null);
    setOfferOverride(false);
  };

  const memberName = member
    ? `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim() || "(no name)"
    : "";

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-1">
        <BanknotesIcon className="h-7 w-7 text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900">Issue a Reward</h1>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Send a voucher of any amount up to ${MAX_AMOUNT} to an active member. The
        member is emailed the voucher automatically. Every issuance is recorded
        against your name.
      </p>

      <div className="flex gap-1 border-b border-gray-200 mb-5">
        {(
          [
            ["issue", "Issue"],
            ["history", "History"],
          ] as [View, string][]
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setView(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              view === id
                ? "border-primary-600 text-primary-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === "history" && <IssuanceHistory />}

      {/* ── Step 1: choose the member ───────────────────────────────────── */}
      {view === "issue" && step === "choose" && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <label
            htmlFor="adhoc-member-search"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Find the member
          </label>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSubmittedSearch(search);
            }}
          >
            <input
              id="adhoc-member-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email or member number"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="submit"
              disabled={search.trim().length < 2}
              className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              Search
            </button>
          </form>

          {isFetching && (
            <p className="text-sm text-gray-500 mt-4">Searching…</p>
          )}

          {!isFetching && submittedSearch && searchResults && (
            <div className="mt-4 divide-y divide-gray-100 border-t border-gray-100">
              {searchResults.data.length === 0 && (
                <p className="text-sm text-gray-500 py-4">
                  No contacts match “{submittedSearch}”.
                </p>
              )}
              {searchResults.data.map((c) => {
                const active = c.has_active_subscription === true;
                return (
                  <div
                    key={c.id}
                    className="py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {`${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() ||
                          "(no name)"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {c.email ?? "no email on file"}
                        {c.membership_id ? ` · #${c.membership_id}` : ""}
                      </p>
                    </div>
                    {active ? (
                      <button
                        type="button"
                        onClick={() => {
                          setMember(c);
                          setStep("amount");
                        }}
                        className="px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 flex-shrink-0"
                      >
                        Select
                      </button>
                    ) : (
                      // The server refuses these outright; say why here rather than let an admin
                      // pick someone and be rejected two screens later.
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        Not an active member
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Step 2: amount ──────────────────────────────────────────────── */}
      {view === "issue" && step === "amount" && member && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <div className="text-sm text-gray-600">
            Rewarding{" "}
            <span className="font-medium text-gray-900">{memberName}</span> ·{" "}
            {member.email}
          </div>

          <div>
            <label
              htmlFor="adhoc-amount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Amount
            </label>
            <div className="relative w-40">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                $
              </span>
              <input
                id="adhoc-amount"
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="25.00"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>
            {amount.trim() !== "" && !amountValid && (
              <p className="text-xs text-red-600 mt-1">
                Enter an amount between $0.01 and ${MAX_AMOUNT}, to the cent.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="adhoc-scope"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Where it can be redeemed
            </label>
            <select
              id="adhoc-scope"
              value={scopeMode}
              onChange={(e) => {
                setScopeMode(e.target.value as AdhocScopeMode);
                setLocationId("");
              }}
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="unscoped">
                Any participating U.S. LawShield location
              </option>
              <option value="retailer">One specific retail location</option>
            </select>
          </div>

          {scopeMode === "retailer" && (
            <div>
              <label
                htmlFor="adhoc-location"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Retail location
              </label>
              <select
                id="adhoc-location"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={locationsLoading}
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
              >
                <option value="">
                  {locationsLoading ? "Loading locations…" : "Choose a location"}
                </option>
                {(locations ?? []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {l.city ? ` — ${l.city}` : ""}
                    {l.state ? `, ${l.state}` : ""}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Only locations that can actually enforce a restriction are
                listed. A location without a facility ID is left out, because a
                voucher bound to one would promise a limit nothing checks.
              </p>
            </div>
          )}

          <div>
            <label
              htmlFor="adhoc-validity"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Valid for
            </label>
            <select
              id="adhoc-validity"
              value={validityDays}
              onChange={(e) => setValidityDays(e.target.value)}
              className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              {ALLOWED_VALIDITY_DAYS.map((d) => (
                <option key={d} value={String(d)}>
                  {d} days
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="adhoc-notes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Reason (optional)
            </label>
            <textarea
              id="adhoc-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Why this reward is being issued"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Kept in the internal record only. The member never sees it.
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => setStep("choose")}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg"
            >
              Back
            </button>
            <button
              type="button"
              disabled={!amountValid || !validityValid || !scopeValid}
              onClick={goToConfirm}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              Review
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: confirm ─────────────────────────────────────────────── */}
      {view === "issue" && step === "confirm" && member && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Confirm this reward
          </h2>
          <dl className="text-sm divide-y divide-gray-100 border-y border-gray-100 mb-4">
            <div className="flex justify-between py-2">
              <dt className="text-gray-500">Member</dt>
              <dd className="font-medium text-gray-900">{memberName}</dd>
            </div>
            <div className="flex justify-between py-2">
              <dt className="text-gray-500">Email</dt>
              <dd className="text-gray-900">{member.email}</dd>
            </div>
            <div className="flex justify-between py-2">
              <dt className="text-gray-500">Amount</dt>
              <dd className="font-semibold text-gray-900">
                ${amountValue.toFixed(2)}
              </dd>
            </div>
            <div className="flex justify-between py-2 gap-4">
              <dt className="text-gray-500 flex-shrink-0">Redeemable at</dt>
              <dd className="text-gray-900 text-right">
                {scopeMode === "unscoped"
                  ? "Any participating U.S. LawShield location"
                  : ((locations ?? []).find((l) => l.id === locationId)?.name ??
                    "the selected location")}
              </dd>
            </div>
            <div className="flex justify-between py-2">
              <dt className="text-gray-500">Valid for</dt>
              <dd className="text-gray-900">{validityValue} days</dd>
            </div>
          </dl>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-800">{error}</p>
                  {offerOverride && (
                    <button
                      type="button"
                      disabled={issuing}
                      onClick={() => issue(true)}
                      className="mt-2 text-sm font-medium text-red-800 underline"
                    >
                      Issue anyway — this is a deliberate second reward today
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              disabled={issuing}
              onClick={() => setStep("amount")}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg disabled:opacity-50"
            >
              Back
            </button>
            <button
              type="button"
              disabled={issuing}
              onClick={() => issue(false)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {issuing
                ? "Issuing…"
                : `Issue $${amountValue.toFixed(2)} to ${memberName}`}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4: done ────────────────────────────────────────────────── */}
      {view === "issue" && step === "done" && result && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900">
                {result.duplicate
                  ? "Already issued — showing that reward"
                  : "Reward issued"}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                ${amountValue.toFixed(2)} to {memberName}.
                {result.email?.accepted === false && (
                  <>
                    {" "}
                    The email could not be sent
                    {result.email?.reason ? ` (${result.email.reason})` : ""} —
                    the voucher is still valid, so pass the code on another way.
                  </>
                )}
              </p>
              <p className="mt-3 text-sm text-gray-500">Voucher code</p>
              <p className="font-mono text-lg font-semibold text-gray-900">
                {result.voucherCode}
              </p>
              {result.qrImageUrl && (
                <img
                  src={result.qrImageUrl}
                  alt={`QR code for voucher ${result.voucherCode}`}
                  className="mt-3 h-40 w-40 border border-gray-200 rounded"
                />
              )}
              <button
                type="button"
                onClick={reset}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium"
              >
                Issue another
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdhocReward;
